import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, startAfter, onSnapshot } from 'firebase/firestore';

import { useAppContext } from '../AppContext';
import UniversalMediaRenderer from './UniversalMediaRenderer';
import CommentSection from './CommentSection';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import AiToolsButton from './AiToolsButton';
import AiReplyGenerator from './AiReplyGenerator';
import HoverTooltip from './HoverTooltip';
import PostCard from './PostCard';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from './LoadingSpinner';

function NexusFeed({ isMember, nexusId }) {
    const { t } = useTranslation();
    const { handleUserSelect, db, appId, userId, appFunctions, showConfirmation, setMessage } = useAppContext();
    const [posts, setPosts] = useState([]);
    const [lastVisible, setLastVisible] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [loadingAction, setLoadingAction] = useState(null);
    const [aiModalContent, setAiModalContent] = useState(null);
    const [aiModalTitle, setAiModalTitle] = useState('');
    const POSTS_PER_PAGE = 10;

    const fetchPosts = useCallback(async (loadMore = false) => {
        if (!hasMore && loadMore) return;
        setIsLoading(true);

        try {
            const postsRef = collection(db, `artifacts/${appId}/public/data/nexuses/${nexusId}/posts`);
            let q = query(
                postsRef,
                orderBy("timestamp", "desc"),
                limit(POSTS_PER_PAGE)
            );

            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const documentSnapshots = await getDocs(q);
            const newPosts = documentSnapshots.docs.map(d => ({ id: d.id, ...d.data() }));

            setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            setPosts(prev => loadMore ? [...prev, ...newPosts] : newPosts);
            setHasMore(newPosts.length === POSTS_PER_PAGE);
        } catch (error) {
            setMessage(t('nexusFeed.fetchError', { message: error.message }));
        } finally {
            setIsLoading(false);
        }
    }, [db, appId, nexusId, hasMore, lastVisible, setMessage, t]);

    useEffect(() => {
        fetchPosts(false);
    }, [fetchPosts]);

    const handleLike = (postId) => {
        const updateReaction = httpsCallable(appFunctions, 'updateReaction');
        updateReaction({ entryId: postId, reaction: 'like', contentType: 'nexusPost', nexusId }).catch(err => setMessage(`${t('nexusFeed.likeFailed')} ${err.message}`));
    };

    const handleAmplify = (postId) => {
        const cost = 10; // Fixed amplify cost within Nexuses for now
        showConfirmation({
            message: t('nexusFeed.amplifyConfirmation', { cost }), 
            onConfirm: async () => {
                const amplifyFn = httpsCallable(appFunctions, 'amplifyNexusPost');
                try {
                    await amplifyFn({ nexusId, postId, amount: cost });
                    setMessage(t('nexusFeed.amplifySuccess'));
                } catch (error) {
                    setMessage(`${t('nexusFeed.amplifyFailed')} ${error.message}`);
                }
            }
        });
    };

    const handleDelete = (postId) => {
        showConfirmation({
            message: t('nexusFeed.deleteConfirmation'),
            onConfirm: async () => {
                const deleteFn = httpsCallable(appFunctions, 'deleteNexusPost');
                try {
                    await deleteFn({ nexusId, postId });
                    setMessage(t('nexusFeed.deleteSuccess'));
                } catch (error) {
                    setMessage(`${t('nexusFeed.deleteFailed')} ${error.message}`);
                }
            }
        });
    };

    const runAiAction = async (action, postId, content, title) => {
        setLoadingAction(postId + action);
        try {
            const getAnalysis = httpsCallable(appFunctions, 'getAiAnalysis');
            const result = await getAnalysis({ entryId: postId, analysisType: action, content });
            setAiModalTitle(title);
            setAiModalContent(result.data.text);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <div className="space-y-4">
            {aiModalContent && <AIGeneratedContentModal title={aiModalTitle} content={aiModalContent} onClose={() => setAiModalContent(null)} />}
            {posts.length > 0 ? (
                posts.map(post => (
                    <PostCard 
                        key={post.id} 
                        post={post} 
                        context="nexus"
                        userId={userId}
                        onUserSelect={handleUserSelect}
                        handleLikeToggle={handleLike}
                        handleDelete={handleDelete}
                        handleAmplify={handleAmplify}
                    />
                ))
            ) : (
                <div className="text-center text-gray-400 p-8">
                    <LucideIcons.Wind size={48} className="mx-auto mb-4" />
                    <p>{t('nexusFeed.emptyFeed')}</p>
                </div>
            )}
            {hasMore && (
                <div className="text-center mt-4">
                    <button
                        onClick={() => fetchPosts(true)}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full disabled:bg-gray-500"
                    >
                        {isLoading ? t('nexusFeed.loadingMore') : t('nexusFeed.loadMore')}
                    </button>
                </div>
            )}
            {isLoading && posts.length === 0 && <LoadingSpinner />}
        </div>
    );
}

export default NexusFeed;